<template>
    <teleport to="body">
        <transition name="fade">
            <div v-if="modelValue" class="v-popup-overlay" @mousedown="handleMouseDown"
                @click.self="handleOverlayClick">
                <div class="v-popup-content" :class="{ 'overflow-visible': overflowVisible }" :style="{ width: width }">
                    <div class="v-popup-header">
                        <slot name="header">
                            <h3 v-if="title" class="v-popup-title">{{ title }}</h3>
                        </slot>
                    </div>

                    <div class="v-popup-body">
                        <slot></slot>
                    </div>

                    <div v-if="$slots.footer" class="v-popup-footer">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
export default {
    name: 'VPopup',
    data() {
        return {
            isMouseDownOnOverlay: false
        }
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true
        },
        title: {
            type: String,
            default: ''
        },
        width: {
            type: String,
            default: '500px'
        },
        closeOnOverlayClick: {
            type: Boolean,
            default: true
        },
        closeOnEsc: {
            type: Boolean,
            default: true
        },
        overflowVisible: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'close'],
    watch: {
        modelValue(val) {
            if (val && this.closeOnEsc) {
                document.addEventListener('keydown', this.handleKeydown);
            } else {
                document.removeEventListener('keydown', this.handleKeydown);
            }
        }
    },
    mounted() {
        if (this.modelValue && this.closeOnEsc) {
            document.addEventListener('keydown', this.handleKeydown);
        }
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
    },
    methods: {
        handleMouseDown(e) {
            this.isMouseDownOnOverlay = e.target === e.currentTarget;
        },
        handleOverlayClick() {
            if (this.isMouseDownOnOverlay) {
                this.close();
            }
            this.isMouseDownOnOverlay = false;
        },
        close() {
            if (this.closeOnOverlayClick) {
                this.$emit('update:modelValue', false);
                this.$emit('close');
            }
        },
        handleKeydown(e) {
            if (this.closeOnEsc && e.key === 'Escape') {
                this.$emit('update:modelValue', false);
                this.$emit('close');
            }
        }
    }
}
</script>

<style lang="scss">
.v-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: color-mix(in srgb, var(--color-bg-dark), 20%, transparent);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    backdrop-filter: blur(2px);

    .v-popup-content {
        background-color: var(--color-bg-dark);
        border-radius: 12px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
        border: 1px solid var(--color-border);
        max-width: 90vw;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .v-popup-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 20px;
            // border-bottom: 1px solid var(--color-border);

            .v-popup-close {
                background: none;
                border: none;
                cursor: pointer;
                padding: 4px;
                border-radius: 4px;
                color: var(--color-text-light);
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;

                &:hover {
                    background-color: var(--color-bg-light);
                    color: var(--color-text);
                }
            }
        }

        .v-popup-body {
            padding: 20px;
            overflow-y: auto;
            color: var(--color-text);
        }

        .v-popup-footer {
            padding: 20px;
            // border-top: 1px solid var(--color-border);
            display: flex;
            justify-content: space-between;
            gap: 12px;
        }

        &.overflow-visible {
            overflow: visible;

            .v-popup-body {
                overflow: visible;
                // overflow-y: visible; 
            }
        }
    }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;

    .v-popup-content {
        transition: transform 0.3s ease;
    }
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;

    .v-popup-content {
        transform: scale(0.95) translateY(10px);
    }
}
</style>
